import * as React from 'react'
// Lucide doesn't ship a 1:1 for every Phosphor industrial icon —
// `Engine` and `Drop` are the only swaps that lose semantic accuracy.
// Engine → Cog (gear-driven machine), Drop → Droplet (liquid vessel).
import {
  Bot as RobotIcon,
  Box as CubeIcon,
  Cog as EngineIcon,
  Cog as GearIcon,
  Droplet as DropIcon,
  Factory as FactoryIcon,
  Fan as FanIcon,
  Gauge as GaugeIcon,
  Thermometer as ThermometerIcon,
  Wrench as WrenchIcon
} from 'lucide-react'

/**
 * Small mapping from `Equipment.equipment_type` to a Phosphor icon. The
 * field is intentionally free-form (`packages/ontology/core/src/types/schema.ts`
 * declares `KNOWN_EQUIPMENT_TYPES` as recommended values, not an enum),
 * so we match case-insensitively and fall back to a neutral `CubeIcon`
 * for anything we don't recognize.
 *
 * Keys are normalized to lower-case + alpha-only. Add additional aliases
 * here as the graph picks up new types — keep the list short, the goal
 * is "good enough that two random equipment in a list look different".
 */
type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>

const ICONS_BY_TYPE: Record<string, IconComponent> = {
  vessel: DropIcon,
  tank: DropIcon,
  heatexchanger: ThermometerIcon,
  pump: EngineIcon,
  valve: GearIcon,
  motor: EngineIcon,
  conveyor: FactoryIcon,
  actuator: WrenchIcon,
  roboticcell: RobotIcon,
  robot: RobotIcon,
  workstation: WrenchIcon,
  instrument: GaugeIcon,
  controlloop: GearIcon,
  drive: GaugeIcon,
  pipe: CubeIcon,
  fan: FanIcon,
  other: CubeIcon
}

export function equipmentIconFor(equipmentType: string | undefined): IconComponent {
  if (!equipmentType) return CubeIcon
  const key = equipmentType.toLowerCase().replace(/[\s_-]/g, '')
  return ICONS_BY_TYPE[key] ?? CubeIcon
}
